/* eslint-disable react/destructuring-assignment */
import React, { StrictMode, useRef } from 'react';
import { ErrorBoundary } from 'components/ErrorBoundary';
import BadgeLabel from 'components/BadgeLabel';
import { connect } from 'react-redux';
import Link from 'components/Link';
import { getStandardSubnav, subnavResponseItemsSchema } from 'lib/types/subnav';
import { useAssertValid } from 'lib/Hooks/useAssertValid';
import { useOverscroll } from 'lib/Hooks/useOverscroll';
import { list } from 'lib/types/list';
import styles from './styles.module.scss';

/**
 * @typedef {{
 *   links: import('lib/types/subnav').SubnavResponseItem[];
 * }} QuickLinksProps
 */

/**
 * @param {import('lib/types/subnav').SubnavItem} subnavItem
 */
function notBlank(subnavItem) {
  return subnavItem.text && subnavItem.url;
}

/**
 * Presentational component for displaying quick links.
 *
 * @param {object} props
 * @param {string} props.theme
 * @param {QuickLinksProps['links']} props.links
 */
export function QuickLinksDisplay(props) {
  const scrollContainerRef = useRef(/** @type {HTMLUListElement | null} */ (null));
  const [isValid, validLinks] = useAssertValid(subnavResponseItemsSchema, props.links);
  const { left, right = 0 } = useOverscroll(scrollContainerRef);

  if (!isValid) {
    return null;
  }

  const links = getStandardSubnav(list(validLinks)).filter(notBlank);

  if (links.length < 2) {
    return null;
  }

  const scrollContainerStyle = /** @type {React.CSSProperties} */ ({
    '--overscroll-left': left,
    '--overscroll-right': right,
  });

  return (
    <nav
      className={`${styles.quickLinks} ${props.theme} quick-links`}
      data-activity-map="quick-links"
    >
      <div className={styles.scrollContainer} style={scrollContainerStyle}>
        <div className={styles.shadow} />
        <ul ref={scrollContainerRef}>
          {links.map((link, index) => (
            <li key={link.text}>
              <Link href={link.url} data-icid={`quick-links-${index}`}>
                {link.badge ? <BadgeLabel badgeType={link.badge} /> : null}
                <span className={styles.caption}>{link.text}</span>
              </Link>
            </li>
          ))}
          <li className={styles.tipLine}>
            <Link href="/tips/" data-icid={`quick-links-${links.length}`}>
              <span className={styles.caption}>Tipline</span>
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}

/**
 * @param {{ navbar: {theme: string}, shared: {isChromeless: boolean }}} state
 */
const mapStateToProps = ({ navbar, shared }) => ({
  theme: shared.isChromeless ? 'dark' : navbar.theme,
});

/** @type {import('react-redux').ConnectedComponent<typeof QuickLinksDisplay, QuickLinksProps>} */
const QuickLinksConnected = connect(mapStateToProps)(QuickLinksDisplay);

/**
 * @param {QuickLinksProps} props
 */
export function QuickLinks({ links }) {
  return (
    <StrictMode>
      <ErrorBoundary>
        <QuickLinksConnected links={links} />
      </ErrorBoundary>
    </StrictMode>
  );
}
